import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Message } from 'primereact/message';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { WorkflowTabBar } from '@/components/common';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatEmbryoName } from '@/grid/value-formatters';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  applyImplantBillingDefault,
  BULK_LOCKED_FIELDS,
  BULK_LOCKED_PAYLOAD_KEYS,
  bulkEmbryoEditTabs,
  EmbryoAdditionalTab,
  EmbryoCalvesTab,
  type EmbryoEditTab,
  embryoEditTabs,
  EmbryoFreezingTab,
  EmbryoGeneralTab,
  EmbryoImplantTab,
  EmbryoMeritInfoTab,
  EmbryoNotesTab,
  EmbryoQCTab,
  EmbryoRecipStatusTab,
  LOCKED_IDENTIFIER_FIELDS,
  outsideEmbryoEditTabs,
  resolveEmbryoFormData,
  useAccountDataMap,
  useEmbryoFormValidation,
} from '@/pages/embryo-app/components/EmbryoForm';
import {
  type EmbryoFormData,
  useEmbryoValidationSchema,
} from '@/pages/embryo-app/schema';
import { hasPermission, MODULES, useCurrentUser } from '@/services/auth/util';
import {
  type EditEmbryoInput,
  type EditOutsideEmbryoInput,
  type EmbryoDetailDto,
  type EmbryoPayload,
  embryoServiceApi,
  useEditEmbryos,
  useEditOutsideEmbryos,
} from '@/services/embryo-service';
import { useToast } from '@/services/toast';

interface EditEmbryoDialogProps {
  visible: boolean;
  selectedEmbryos: EmbryoDetailDto[];
  onHide: () => void;
  onSuccess: (failedEmbryos?: EmbryoDetailDto[]) => void;
  initialTab?: EmbryoEditTab;
}

export const EditEmbryoDialog = ({
  visible,
  selectedEmbryos,
  onHide,
  onSuccess,
  initialTab,
}: EditEmbryoDialogProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const location = useLocation();
  const { data: currentUser } = useCurrentUser();
  const hasManagerWrite = hasPermission(
    currentUser,
    MODULES.EMBRYO_MANAGER_WRITE,
  );
  const isOutsideEmbryo = location.pathname.includes('outside-embryos');
  const [activeTab, setActiveTab] = useState<EmbryoEditTab>(
    initialTab ?? 'general',
  );
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [fetchedStatuses, setFetchedStatuses] = useState<number[]>([]);
  const [fetchedEmbryoIds, setFetchedEmbryoIds] = useState<Map<string, string>>(
    new Map(),
  );
  const originalStateCodeRef = useRef<number | undefined>(undefined);
  const originalOwnerIdRef = useRef<string | undefined>(undefined);
  const accountDataMap = useAccountDataMap();

  const isBulkMode = selectedEmbryos.length > 1;
  const singleEmbryo = selectedEmbryos.length === 1 ? selectedEmbryos[0] : null;
  const implantBillingDefault = useFeatureFlag(
    'Implant_Billing_Account_Default',
  );
  const bulkLockedFieldsEnabled = useFeatureFlag(
    'Embryo_Bulk_Edit_Locked_Fields',
  );

  const schema = useEmbryoValidationSchema();

  const methods = useForm<EmbryoFormData>({
    resolver: (values, context, options) => {
      const sanitized = JSON.parse(
        JSON.stringify(values, (_, v) => (v === null ? undefined : v)),
      );
      if (isOutsideEmbryo) {
        delete sanitized.status;
      }
      return zodResolver(schema)(sanitized, context, options);
    },
    mode: 'onChange',
  });

  const { mutate: editEmbryos } = useEditEmbryos();
  const { mutate: editOutsideEmbryos } = useEditOutsideEmbryos();

  const { handleSubmit, reset, setValue } = methods;

  useEffect(() => {
    if (visible) {
      setActiveTab(initialTab ?? 'general');
    } else {
      reset({});
      setFetchedStatuses([]);
      setFetchedEmbryoIds(new Map());
      originalStateCodeRef.current = undefined;
      originalOwnerIdRef.current = undefined;
    }
  }, [visible, initialTab, reset]);

  useEffect(() => {
    if (!visible) return;

    if (singleEmbryo) {
      setIsLoadingData(true);
      embryoServiceApi
        .getEmbryoById(singleEmbryo.id, isOutsideEmbryo)
        .then((embryo) => {
          if (embryo) {
            reset(resolveEmbryoFormData(embryo) as any);
            if (implantBillingDefault) {
              applyImplantBillingDefault(setValue, embryo);
            }
            setFetchedEmbryoIds(new Map([[singleEmbryo.id, embryo.id]]));
            originalStateCodeRef.current = embryo.stateCode;
            originalOwnerIdRef.current = embryo.ownerId;
          }
        })
        .catch((error) => {
          console.error('Failed to fetch embryo data:', error);
          toast.show({
            severity: 'error',
            summary: t('common.error'),
            detail: t('embryo.failedToLoadEmbryo', {
              defaultValue: 'Failed to load embryo data',
            }),
            life: 3000,
          });
        })
        .finally(() => {
          setIsLoadingData(false);
        });
    } else if (isBulkMode) {
      setIsLoadingData(true);
      Promise.all(
        selectedEmbryos.map((e) =>
          embryoServiceApi.getEmbryoById(e.id, isOutsideEmbryo),
        ),
      )
        .then((results) => {
          const statuses = results
            .filter((r): r is NonNullable<typeof r> => r !== null)
            .map((r) => r.status)
            .filter((s): s is number => s != null);
          setFetchedStatuses(statuses);
          const idMap = new Map<string, string>();
          results.forEach((r, i) => {
            if (r && selectedEmbryos[i]) {
              idMap.set(selectedEmbryos[i].id, r.id);
            }
          });
          setFetchedEmbryoIds(idMap);
        })
        .catch((error) => {
          console.error('Failed to fetch bulk embryo statuses:', error);
        })
        .finally(() => {
          setIsLoadingData(false);
        });
      reset({});
    }
  }, [
    visible,
    singleEmbryo,
    isBulkMode,
    selectedEmbryos,
    isOutsideEmbryo,
    reset,
    toast,
    t,
    implantBillingDefault,
  ]);

  const currentStatuses = useMemo(
    () =>
      fetchedStatuses.length > 0
        ? fetchedStatuses
        : selectedEmbryos
            .map((e) => e.status)
            .filter((s): s is number => s !== null && s !== undefined),
    [fetchedStatuses, selectedEmbryos],
  );

  const dialogTitle = useMemo(() => {
    if (isBulkMode) {
      return t('embryo.editSelected', {
        count: selectedEmbryos.length,
        defaultValue: `Edit ${selectedEmbryos.length} Embryos`,
      });
    }
    return t('embryo.editEmbryo', {
      name: singleEmbryo?.name || singleEmbryo?.id || '',
      defaultValue: `Edit Embryo ${singleEmbryo?.name || ''}`,
    });
  }, [isBulkMode, selectedEmbryos.length, singleEmbryo, t]);

  const {
    isSaveDisabled: validationDisabled,
    normalizeForSubmit,
    commentRequiredMissing,
    transactionFieldsMissing,
    ownerOrSoldToMissing: _ownerOrSoldToMissing,
    ownerPastDueBlocked: _ownerPastDueBlocked,
    statusMessages,
  } = useEmbryoFormValidation({
    methods,
    currentStatuses,
    isBulkMode,
    selectedEmbryos,
    originalStateCode: originalStateCodeRef.current,
    isOutsideEmbryo,
    accountDataMap,
    originalOwnerId: originalOwnerIdRef.current,
  });

  const isSaveDisabled = isLoadingData || validationDisabled;

  const showDeactivateGuide =
    visible &&
    !isLoadingData &&
    (initialTab === 'additional' || commentRequiredMissing);

  const onSubmit = useCallback(
    (data: EmbryoFormData) => {
      const RELATIONAL_KEYS = [
        'donor',
        'sire',
        'semenId',
        'probableDonorId',
        'donorAge',
        'preBook',
        'breedId',
      ];
      // Outside embryos are exempt: their bulk edit exists to set donor/sire
      // matings, which the wider lock list would block.
      const bulkLockActive =
        isBulkMode && bulkLockedFieldsEnabled && !isOutsideEmbryo;

      const lockedForBulk = bulkLockActive
        ? (BULK_LOCKED_FIELDS as readonly string[])
        : (LOCKED_IDENTIFIER_FIELDS as readonly string[]);

      const rawPayload = {
        ...Object.entries(data).reduce(
          (acc, [key, value]) => {
            if (isBulkMode && lockedForBulk.includes(key)) return acc;
            if (RELATIONAL_KEYS.includes(key)) return acc;
            if (value !== undefined && value !== null && value !== '') {
              acc[key as keyof EmbryoFormData] = value;
            }
            return acc;
          },
          {} as Partial<EmbryoFormData> & {
            donorId?: string;
            sireId?: string;
            semenId?: string;
          },
        ),
        ...normalizeForSubmit(data),
      };

      /**
       * Drop locked keys from the *final* payload (WI 37965).
       *
       * Filtering inside the reduce above is not enough: `normalizeForSubmit`
       * is spread afterwards and re-emits `donor`/`sire` as `donorId`/`sireId`
       * plus every date field — which would put `creation`, `modifiedOn` and
       * the recip heat dates straight back on the request.
       */
      const payload = bulkLockActive
        ? (Object.fromEntries(
            Object.entries(rawPayload).filter(
              ([key]) =>
                !(BULK_LOCKED_PAYLOAD_KEYS as readonly string[]).includes(key),
            ),
          ) as typeof rawPayload)
        : rawPayload;

      if (Object.keys(payload).length === 0) {
        toast.show({
          severity: 'warn',
          summary: t('common.warning'),
          detail: t('embryo.noChangesDetectedBulkEdit', {
            defaultValue: 'No changes detected',
          }),
          life: 3000,
        });
        return;
      }

      const mutationCallbacks = {
        onSuccess: async (results: EmbryoPayload[]) => {
          const succeededCount = results.filter((r) => r.success).length;
          const failedEmbryos = selectedEmbryos.filter(
            (_, i) => !results[i]?.success,
          );

          if (failedEmbryos.length === 0) {
            toast.show({
              severity: 'success',
              summary: t('common.success'),
              detail: t('embryo.embryosUpdated', {
                count: succeededCount,
                defaultValue: `${succeededCount} embryo(s) updated successfully`,
              }),
              life: 3000,
            });
            onSuccess();
            onHide();
            return;
          }

          const errorMessages = results
            .map((r) => (!r.success ? r.message : null))
            .filter(Boolean);
          const uniqueErrors = [...new Set(errorMessages)];
          const toastSummary = uniqueErrors
            .map((msg) => {
              const count = errorMessages.filter((m) => m === msg).length;
              return count > 1 ? `${msg} (${count})` : msg;
            })
            .join('; ');

          if (succeededCount === 0) {
            toast.show({
              severity: 'error',
              summary: t('common.error'),
              detail:
                toastSummary ||
                `${failedEmbryos.length} embryo(s) failed to update`,
              life: 8000,
            });
            return;
          }

          toast.show({
            severity: 'warn',
            summary: t('common.warning'),
            detail: `${succeededCount} embryo(s) updated, ${failedEmbryos.length} failed. ${toastSummary}`,
            life: 8000,
          });
          onSuccess(failedEmbryos);
          onHide();
        },
        onError: (error: Error) => {
          console.error('Failed to edit embryos:', error);
          toast.show({
            severity: 'error',
            summary: t('common.error'),
            detail: t('embryo.failedToUpdateEmbryos', {
              defaultValue: 'Failed to update embryos',
            }),
            life: 3000,
          });
        },
      };

      if (isOutsideEmbryo) {
        const OUTSIDE_EDITABLE_PAYLOAD_KEYS = new Set([
          'donorId',
          'sireId',
          'breedId',
          'name',
        ]);
        const filteredPayload = Object.fromEntries(
          Object.entries(payload).filter(([key]) =>
            OUTSIDE_EDITABLE_PAYLOAD_KEYS.has(key),
          ),
        );

        const outsideInputs: EditOutsideEmbryoInput[] = selectedEmbryos.map(
          (embryo) => ({
            outsideEntryId: embryo.id,
            ...filteredPayload,
          }),
        );

        editOutsideEmbryos(outsideInputs, mutationCallbacks);
      } else {
        const inputs: EditEmbryoInput[] = selectedEmbryos.map((embryo) => ({
          embryoId: fetchedEmbryoIds.get(embryo.id) ?? embryo.id,
          ...payload,
        }));

        editEmbryos(inputs, mutationCallbacks);
      }
    },
    [
      selectedEmbryos,
      fetchedEmbryoIds,
      currentStatuses,
      editEmbryos,
      editOutsideEmbryos,
      isOutsideEmbryo,
      toast,
      t,
      onSuccess,
      onHide,
      isBulkMode,
      bulkLockedFieldsEnabled,
      accountDataMap,
      currentUser,
    ],
  );

  const handleCancel = useCallback(() => {
    reset({});
    setFetchedStatuses([]);
    setFetchedEmbryoIds(new Map());
    originalStateCodeRef.current = undefined;
    originalOwnerIdRef.current = undefined;
    setActiveTab(initialTab ?? 'general');
    onHide();
  }, [reset, onHide]);

  const handleInvalidSubmit = useCallback(
    (errors: Record<string, any>) => {
      const firstMessage = Object.values(errors)
        .map((e: any) => e?.message)
        .find(Boolean);
      toast.show({
        severity: 'error',
        summary: t('common.validationError', {
          defaultValue: 'Validation Error',
        }),
        detail:
          firstMessage ??
          t('embryo.pleaseFixErrors', {
            defaultValue: 'Please fix the errors before saving',
          }),
        life: 5000,
      });
    },
    [toast, t],
  );

  const renderFooter = () => (
    <>
      <Button variant="outline" onClick={handleCancel}>
        {t('common.cancel')}
      </Button>
      <Button
        variant="default"
        onClick={handleSubmit(onSubmit, handleInvalidSubmit)}
        disabled={isSaveDisabled}
      >
        {isLoadingData && (
          <i className="pi pi-spin pi-spinner mr-1.5 text-xs" />
        )}
        {t('common.save')}
      </Button>
    </>
  );

  const filteredOutsideEmbryoEditTabs = outsideEmbryoEditTabs.filter(
    (tab) => tab.key !== 'implant' && tab.key !== 'freezing',
  );

  return (
    <Dialog
      open={visible}
      onOpenChange={(o) => {
        if (!o) handleCancel();
      }}
    >
      <DialogContent className="edit-embryo-dialog flex max-h-[calc(100vh-4rem)] flex-col md:w-[60vw] md:max-w-[60vw] 2xl:w-[80vw] 2xl:max-w-[80vw]">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <div className="mb-3 min-h-8 w-full rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          {selectedEmbryos.map((e) => formatEmbryoName(e) || e.id).join(', ')}
        </div>

        {isLoadingData ? (
          <div className="flex justify-center p-4">
            <span className="inline-block h-8 w-8 animate-spin rounded-full border-3 border-gray-400 border-t-transparent" />
          </div>
        ) : (
          <FormProvider {...methods}>
            <div className="flex h-full flex-col gap-2">
              {showDeactivateGuide && (
                <Message
                  severity="info"
                  text={`${t('embryo.deactivateEmbryo')}: ${t('embryo.deactivateGuideMessage')}`}
                  className="w-full justify-start"
                />
              )}
              {statusMessages.map((msg, i) => (
                <Message
                  key={`${msg.severity}-${i}`}
                  severity={msg.severity}
                  text={`${msg.summary}: ${msg.detail}`}
                  className="w-full justify-start"
                />
              ))}
              {transactionFieldsMissing && (
                <Message
                  severity="warn"
                  text={`${t('common.warning')}: ${t(
                    'embryo.transactionFieldsRequired',
                    {
                      defaultValue:
                        'Transaction Date and Transaction Comment are required when Sold To is filled.',
                    },
                  )}`}
                  className="w-full justify-start"
                />
              )}

              <div className="flex flex-col gap-2 bg-white p-2">
                <WorkflowTabBar
                  tabs={
                    isOutsideEmbryo
                      ? filteredOutsideEmbryoEditTabs
                      : isBulkMode
                        ? bulkEmbryoEditTabs
                        : embryoEditTabs
                  }
                  activeTab={activeTab}
                  onTabChange={(tab) => setActiveTab(tab)}
                  className="w-full"
                />
              </div>

              <div className="flex-1 overflow-auto p-4">
                {activeTab === 'general' && (
                  <EmbryoGeneralTab
                    control={methods.control}
                    watch={methods.watch}
                    isBulkMode={isBulkMode}
                    hasManagerWrite={hasManagerWrite}
                    isOutsideEmbryo={isOutsideEmbryo}
                  />
                )}
                {activeTab === 'implant' && (
                  <EmbryoImplantTab
                    control={methods.control}
                    watch={methods.watch}
                    isBulkMode={isBulkMode}
                    hasManagerWrite={hasManagerWrite}
                    isOutsideEmbryo={isOutsideEmbryo}
                  />
                )}
                {activeTab === 'freezing' && (
                  <EmbryoFreezingTab
                    control={methods.control}
                    watch={methods.watch}
                    setValue={methods.setValue}
                    isBulkMode={isBulkMode}
                    isOutsideEmbryo={isOutsideEmbryo}
                  />
                )}
                {!isOutsideEmbryo && activeTab === 'qc' && (
                  <EmbryoQCTab
                    control={methods.control}
                    isBulkMode={isBulkMode}
                  />
                )}
                {activeTab === 'additional' && (
                  <EmbryoAdditionalTab
                    control={methods.control}
                    watch={methods.watch}
                    setValue={methods.setValue}
                    isBulkMode={isBulkMode}
                    hasManagerWrite={hasManagerWrite}
                    originalStateCode={originalStateCodeRef.current}
                    isOutsideEmbryo={isOutsideEmbryo}
                  />
                )}
                {!isOutsideEmbryo && activeTab === 'meritInfo' && (
                  <EmbryoMeritInfoTab
                    control={methods.control}
                    isBulkMode={isBulkMode}
                    columns={3}
                  />
                )}
                {!isOutsideEmbryo && activeTab === 'recipStatus' && (
                  <EmbryoRecipStatusTab
                    control={methods.control}
                    watch={methods.watch}
                    isBulkMode={isBulkMode}
                    hasManagerWrite={hasManagerWrite}
                    columns={3}
                  />
                )}
                {!isOutsideEmbryo && activeTab === 'calves' && (
                  <EmbryoCalvesTab
                    embryoIds={selectedEmbryos.map(
                      (e) => fetchedEmbryoIds.get(e.id) ?? e.id,
                    )}
                  />
                )}
                {!isOutsideEmbryo && activeTab === 'notes' && (
                  <EmbryoNotesTab
                    embryoId={
                      singleEmbryo
                        ? (fetchedEmbryoIds.get(singleEmbryo.id) ??
                          singleEmbryo.id)
                        : undefined
                    }
                  />
                )}
              </div>
            </div>
          </FormProvider>
        )}
        <DialogFooter>{renderFooter()}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
